import {
  Box,
  Button,
  ButtonVariants,
  CurrencyIcon,
  FormControlSizes,
  HStack,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Text,
  useMixpanel,
  useSecuritiesContext,
  type Column,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { createColorBarCellRenderer } from '../../components/ColorBar';
import { PrimeScenarioButton } from '../../components/PrimeScenarioButton';
import { formatScenarioLabel } from '../../utils';
import { DEFAULT_MARKER_COLOR } from './ScenarioChart';
import type { ScenarioComparisonRow } from './utils';

interface UseScenarioColumnsProps {
  hasMultipleSymbols: boolean;
  onShowDetails: (index: number) => void;
  onUseScenario: (index: number, source: MixpanelEventSource) => void;
  /** null when there is no data to scale against; bars render at 0 width */
  sharedMaxValue: number | null;
  colorMap: ReadonlyMap<string, string>;
}

// NOTE: These colors are intentionally hardcoded to match the current Figma spec.
// TODO: Replace with design tokens when these values are exposed by the design system.
const SCENARIO_COLORS = {
  versionBadge: '#E5E5E614',
  unit: '#E5E5E685',
  total: '#E5E5E63D',
  cost: '#F05536B8',
  risk: '#DA9846B8',
} as const;

export const useScenarioColumns = ({
  hasMultipleSymbols,
  onShowDetails,
  onUseScenario,
  sharedMaxValue,
  colorMap,
}: UseScenarioColumnsProps): Column[] => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const mixpanel = useMixpanel();

  const scenarioCellRenderer = useCallback(
    ({ data }: ICellRendererParams<ScenarioComparisonRow>) => {
      if (!data) {
        return null;
      }
      const color = colorMap.get(data.requestId) ?? DEFAULT_MARKER_COLOR;
      return (
        <HStack gap="spacing8" alignItems="center" h="100%">
          <Box
            w="spacing8"
            h="spacing8"
            borderRadius="50%"
            flex="0 0 auto"
            background={color}
            data-testid={`scenario-color-dot-${data.index}`}
            data-color={color}
          />
          <Text size="fontSizeSmall">{formatScenarioLabel(data.version, data.name)}</Text>
        </HStack>
      );
    },
    [colorMap]
  );

  const symbolCellRenderer = useCallback(
    ({ data }: ICellRendererParams<ScenarioComparisonRow>) => {
      if (!data) {
        return null;
      }
      const securityBaseCurrency = securitiesBySymbol.get(data.symbol)?.BaseCurrency;
      return (
        <HStack
          gap="spacing8"
          alignItems="center"
          h="100%"
          color="gray.100"
          data-testid={`scenario-symbol-cell-${data.index}-${data.symbol}`}
        >
          {securityBaseCurrency && (
            <CurrencyIcon
              currency={securityBaseCurrency}
              size={16}
              data-testid={`scenario-symbol-currency-${data.index}-${securityBaseCurrency}`}
            />
          )}
          <Text size="fontSizeSmall" data-testid={`scenario-symbol-text-${data.index}-${data.symbol}`}>
            {data.symbol}
          </Text>
        </HStack>
      );
    },
    [securitiesBySymbol]
  );

  const totalCellRenderer = useMemo(
    () =>
      createColorBarCellRenderer<ScenarioComparisonRow>({
        field: 'total',
        color: SCENARIO_COLORS.total,
        maxValue: sharedMaxValue,
        unit: 'BPS',
        unitColor: SCENARIO_COLORS.unit,
      }),
    [sharedMaxValue]
  );

  const costCellRenderer = useMemo(
    () =>
      createColorBarCellRenderer<ScenarioComparisonRow>({
        field: 'cost',
        color: SCENARIO_COLORS.cost,
        maxValue: sharedMaxValue,
        unit: 'BPS',
        unitColor: SCENARIO_COLORS.unit,
      }),
    [sharedMaxValue]
  );

  const riskCellRenderer = useMemo(
    () =>
      createColorBarCellRenderer<ScenarioComparisonRow>({
        field: 'risk',
        color: SCENARIO_COLORS.risk,
        unit: 'BPS',
        maxValue: sharedMaxValue,
        unitColor: SCENARIO_COLORS.unit,
      }),
    [sharedMaxValue]
  );

  const actionsCellRenderer = useCallback(
    ({ data }: ICellRendererParams<ScenarioComparisonRow>) => {
      if (!data) {
        return null;
      }
      return (
        <HStack gap="spacing8" justifyContent="flex-end" alignItems="center" h="100%">
          <Button
            variant={ButtonVariants.Default}
            size={FormControlSizes.Small}
            onClick={e => {
              e.stopPropagation();
              mixpanel.track(MixpanelEvent.ShowDetails, {
                [MixpanelEventProperty.Source]: MixpanelEventSource.PreTradeScenarios,
              });
              onShowDetails(data.index);
            }}
            startIcon={IconName.EyeShow}
            data-testid={`pre-trade-show-details-${data.index}`}
          >
            Show Details
          </Button>
          <PrimeScenarioButton
            onClick={e => {
              e.stopPropagation();
              onUseScenario(data.index, MixpanelEventSource.PreTradeScenarios);
            }}
          />
        </HStack>
      );
    },
    [mixpanel, onShowDetails, onUseScenario]
  );

  return useMemo<Column[]>(
    () => [
      {
        type: 'custom',
        id: 'scenario',
        field: 'name',
        title: 'SCENARIOS',
        sortable: false,
        flex: 1,
        minWidth: 160,
        maxWidth: 300,
        params: {
          cellRenderer: scenarioCellRenderer,
        },
      },
      ...(hasMultipleSymbols
        ? [
            {
              type: 'custom',
              id: 'symbol',
              field: 'symbol',
              title: 'SYMBOL',
              sortable: false,
              width: 120,
              params: {
                cellRenderer: symbolCellRenderer,
              },
            } satisfies Column,
          ]
        : []),
      {
        type: 'custom',
        id: 'total',
        field: 'total',
        title: 'TOTAL',
        sortable: false,
        frozen: true,
        suppressHeaderMenuButton: true,
        flex: 1,
        minWidth: 120,
        params: {
          cellRenderer: totalCellRenderer,
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
        },
      },
      {
        type: 'custom',
        id: 'cost',
        field: 'cost',
        title: 'COST (Spread + Temp.)',
        sortable: false,
        frozen: true,
        suppressHeaderMenuButton: true,
        flex: 1,
        minWidth: 120,
        params: {
          cellRenderer: costCellRenderer,
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
        },
      },
      {
        type: 'custom',
        id: 'risk',
        field: 'risk',
        title: 'RISK',
        sortable: false,
        frozen: true,
        suppressHeaderMenuButton: true,
        flex: 1,
        minWidth: 120,
        params: {
          cellRenderer: riskCellRenderer,
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
        },
      },
      {
        type: 'custom',
        id: 'actions',
        field: 'index',
        title: '',
        sortable: false,
        pinned: 'right',
        frozen: true,
        suppressHeaderMenuButton: true,
        width: 240,
        params: {
          cellRenderer: actionsCellRenderer,
        },
      },
    ],
    [
      hasMultipleSymbols,
      actionsCellRenderer,
      costCellRenderer,
      scenarioCellRenderer,
      riskCellRenderer,
      symbolCellRenderer,
      totalCellRenderer,
    ]
  );
};
